import { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from "date-fns";
import MessagesPanel from "@/components/MessagesPanel";
import { FileText, Siren, FolderOpen, Stethoscope, Star, CalendarDays, Users, ShieldCheck, Download, Plus, Check, Search, ChevronLeft, ChevronRight, Lock, Upload, Trash2, X, Send, MessageSquare, LayoutDashboard, Activity, AlertTriangle, ClipboardList, UserCheck, ArrowRightLeft } from "lucide-react";
import DashboardFilters from "@/components/DashboardFilters";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/registrations", label: "Registrations", icon: FileText },
  { to: "/admin/procedure-requests", label: "Surgeon Requests", icon: Send },
  { to: "/admin/emergency", label: "Emergency", icon: Siren },
  { to: "/admin/documents", label: "Documents", icon: FolderOpen },
  { to: "/admin/postop", label: "Post-Op", icon: Stethoscope },
  { to: "/admin/reviews", label: "Ratings", icon: Star },
  { to: "/admin/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/audit", label: "Audit", icon: ShieldCheck },
];

export default function AdminDashboard() {
  return (
    <AppShell items={NAV}>
      <Routes>
        <Route index element={<DashboardTab />} />
        <Route path="registrations" element={<RegistrationsTab />} />
        <Route path="procedure-requests" element={<ProcedureRequestsTab />} />
        <Route path="emergency" element={<EmergencyTab />} />
        <Route path="documents" element={<DocumentsTab />} />
        <Route path="postop" element={<PostOpTab />} />
        <Route path="reviews" element={<RatingsTab />} />
        <Route path="schedule" element={<ScheduleTab />} />
        <Route path="messages" element={<MessagesTab />} />
        <Route path="users" element={<UsersTab />} />
        <Route path="audit" element={<AuditTab />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AppShell>
  );
}

function downloadBlob(url, filename) {
  return api.get(url, { responseType: "blob" }).then((res) => {
    const u = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u);
  });
}

// -------- Dashboard --------
function StatCard({ to, icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "from-white to-slate-50 text-slate-900 border-slate-200",
    teal: "from-[#339999]/10 to-white text-[#1E6B6B] border-[#339999]/30",
    amber: "from-amber-50 to-white text-amber-900 border-amber-200",
    red: "from-red-50 to-white text-red-900 border-red-200",
    green: "from-emerald-50 to-white text-emerald-900 border-emerald-200",
  };
  const inner = (
    <div className={`relative rounded-lg border bg-gradient-to-br ${tones[tone]} p-4 h-full transition-shadow hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="text-3xl font-semibold mt-2" style={{ fontFamily: "Outfit" }}>{value ?? 0}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
  if (to) return <Link to={to} className="block">{inner}</Link>;
  return inner;
}

function DashboardTab() {
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!range) return;
    setLoading(true);
    api.get("/admin/dashboard", { params: range })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    const iv = setInterval(() => {
      api.get("/admin/dashboard", { params: range }).then((r) => setData(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, [range?.start_date, range?.end_date]);

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="aamg-label">Operations snapshot</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Dashboard</h1>
          <p className="text-xs text-slate-500 mt-1 italic">Compassionate Care. Clinical Excellence.</p>
        </div>
        <Button
          variant="outline"
          className="border-[#339999]/40 text-[#339999] hover:bg-[#339999]/5"
          onClick={() => downloadBlob("/admin/training-deck", "AAMG-Portal-Training.pptx")}
          data-testid="download-training-deck-btn"
        >
          <Download className="h-4 w-4 mr-2" />Training deck (.pptx)
        </Button>
      </div>

      <Card className="aamg-card"><CardContent className="p-4">
        <DashboardFilters value={range} onChange={setRange} />
      </CardContent></Card>

      {loading && !data ? (
        <div className="text-sm text-slate-400 py-10 text-center">Loading snapshot…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dashboard-stats">
            <StatCard to="/admin/registrations" icon={FileText} tone="teal" label="Pre-Op Registrations"
              value={data.registrations.total}
              sub={`${data.registrations.submitted} submitted · ${data.registrations.reviewed} reviewed`} />
            <StatCard to="/admin/procedure-requests" icon={Send} tone={data.surgeon_requests.pending > 0 ? "amber" : "default"} label="Surgeon Requests"
              value={data.surgeon_requests.total}
              sub={`${data.surgeon_requests.pending} pending · ${data.surgeon_requests.emergent} emergent`} />
            <StatCard to="/admin/emergency" icon={Siren} tone={data.emergency_cases.open > 0 ? "red" : "default"} label="Emergency Cases"
              value={data.emergency_cases.total}
              sub={`${data.emergency_cases.open} open · ${data.emergency_cases.accepted} accepted`} />
            <StatCard to="/admin/schedule" icon={CalendarDays} tone="teal" label="Procedures Scheduled"
              value={data.procedures.scheduled}
              sub={`Top: ${data.procedures.top_anesthetists[0]?.name || "—"}`} />
            <StatCard icon={UserCheck} tone="green" label="On Duty Today"
              value={data.on_duty_today.count}
              sub={data.on_duty_today.users.map((u) => u.name).slice(0, 3).join(", ") || "no one assigned"} />
            <StatCard to="/admin/schedule" icon={ArrowRightLeft} tone={data.swaps.pending > 0 ? "amber" : "default"} label="Swap Requests"
              value={data.swaps.pending}
              sub={`${data.swaps.accepted_in_range} accepted in range`} />
            <StatCard to="/admin/postop" icon={Stethoscope} label="Post-Op Reviews"
              value={data.postop.total}
              sub={`${data.postop.pending_review} pending`} />
            <StatCard to="/admin/documents" icon={FolderOpen} label="Documents Uploaded"
              value={data.documents_uploaded} sub="In selected range" />
            <StatCard to="/admin/reviews" icon={Star} label="Patient Ratings"
              value={data.patient_ratings} sub="In selected range" />
            <StatCard to="/admin/users" icon={Users} label="Active Users"
              value={data.users.active}
              sub={Object.entries(data.users.by_role).map(([r, c]) => `${c} ${r}`).join(" · ")} />
            <StatCard to="/admin/audit" icon={Activity} label="Audit Events"
              value={data.audit.events_in_range} sub="In selected range" />
            <StatCard to="/admin/messages" icon={MessageSquare} label="Messaging"
              value="Open" sub="View conversations" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="aamg-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-4 w-4 text-[#339999]" />Top anesthetists by case load</CardTitle></CardHeader>
              <CardContent>
                {data.procedures.top_anesthetists.length === 0 ? (
                  <div className="text-sm text-slate-400">No procedures in this range</div>
                ) : (
                  <ul className="space-y-2">
                    {data.procedures.top_anesthetists.map((a, i) => (
                      <li key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{a.name}</span>
                        <span className="font-semibold text-[#339999]">{a.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="aamg-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-[#339999]" />Recent activity</CardTitle></CardHeader>
              <CardContent>
                {data.audit.recent.length === 0 ? (
                  <div className="text-sm text-slate-400">No recent events</div>
                ) : (
                  <ul className="space-y-2 text-xs">
                    {data.audit.recent.map((e, i) => (
                      <li key={i} className="flex items-start gap-2 border-b border-slate-100 pb-1.5 last:border-0">
                        <span className="text-slate-400 shrink-0">{e.timestamp?.slice(11, 16)}</span>
                        <span className="font-medium text-slate-700 truncate">{e.actor_email || "system"}</span>
                        <span className="text-slate-500 truncate">{e.action}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

// -------- Registrations --------
function RegistrationsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [picked, setPicked] = useState(new Set());
  const refresh = () => api.get("/admin/registrations", { params: status !== "all" ? { status } : {} }).then((r) => { setItems(r.data || []); setPicked(new Set()); });
  useEffect(() => { refresh(); }, [status]);

  const togglePick = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  const toggleAll = () => { setPicked(picked.size === items.length ? new Set() : new Set(items.map((r) => r.registration_id))); };

  const review = async (rid) => { await api.post(`/admin/registrations/${rid}/review`); toast.success("Marked reviewed"); refresh(); setSelected(null); };
  const del = async (rid) => { if (!window.confirm("Delete this registration?")) return; await api.delete(`/admin/registrations/${rid}`); toast.success("Deleted"); refresh(); setSelected(null); };
  const bulkDelete = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} registration(s)?`)) return;
    const r = await api.post("/admin/registrations/bulk-delete", { user_ids: Array.from(picked) });
    toast.success(`Deleted ${r.data.deleted}`); refresh();
  };
  const openEdit = () => { setEditForm({ ...selected }); setEditing(true); };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/registrations/${selected.registration_id}`, editForm);
      toast.success("Updated"); setEditing(false); setSelected(null); refresh();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-registrations">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="aamg-label">Intake</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Pre-Op Registrations</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {picked.size > 0 && <Button variant="outline" className="text-red-600 border-red-200" onClick={bulkDelete} data-testid="reg-bulk-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete {picked.size}</Button>}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="reg-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="reviewed">Reviewed</SelectItem></SelectContent>
          </Select>
          <Button variant="outline" onClick={() => downloadBlob("/admin/registrations/download-excel", "registrations.xlsx")} data-testid="reg-excel-btn"><Download className="h-4 w-4 mr-2" />Excel</Button>
          <Button variant="outline" onClick={() => downloadBlob("/admin/download-records", "aamg_full_export.xlsx")} data-testid="full-export-btn"><Download className="h-4 w-4 mr-2" />Full export</Button>
        </div>
      </div>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        {items.length === 0 ? <div className="p-10 text-center text-slate-400">No registrations</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={picked.size === items.length && items.length > 0} onChange={toggleAll} data-testid="reg-select-all" /></th>
                <th className="text-left px-4 py-2">Patient</th><th className="text-left px-4 py-2">Surgery</th><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Submitted</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>{items.map((r) => (
              <tr key={r.registration_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-2"><input type="checkbox" checked={picked.has(r.registration_id)} onChange={() => togglePick(r.registration_id)} data-testid={`reg-pick-${r.registration_id}`} /></td>
                <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => setSelected(r)}>{r.surname || r.first_name ? `${r.surname || ""}, ${r.first_name || ""}` : r.patient_email}</td>
                <td className="px-4 py-2">{r.surgery_type || "—"}</td>
                <td className="px-4 py-2">{r.surgery_date || "—"}</td>
                <td className="px-4 py-2">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-2"><Badge className={`border capitalize ${r.status === "reviewed" ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{r.status === "reviewed" && <Lock className="h-3 w-3 mr-1" />}{r.status}</Badge></td>
                <td className="px-4 py-2 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setSelected(r)} data-testid={`view-reg-${r.registration_id}`}>View</Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(r.registration_id)} data-testid={`del-reg-${r.registration_id}`}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(false); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="reg-details-dialog">
          {selected && <>
            <DialogHeader><DialogTitle className="text-xl">{selected.surname}, {selected.first_name} {selected.second_name}</DialogTitle><DialogDescription className="text-xs text-slate-500">Pre-Op registration details and attachments</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`border capitalize ${selected.status === "reviewed" ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{selected.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => downloadBlob(`/admin/registrations/${selected.registration_id}/download-pdf`, `preop_${selected.registration_id}.pdf`)} data-testid="reg-pdf-btn"><Download className="h-4 w-4 mr-2" />PDF</Button>
                {!editing && <Button size="sm" variant="outline" onClick={openEdit} data-testid="reg-edit-btn"><Plus className="h-4 w-4 mr-2 rotate-45" />Edit</Button>}
                {selected.status !== "reviewed" && <Button size="sm" className="bg-[#339999] hover:bg-[#2A7F7F]" onClick={() => review(selected.registration_id)} data-testid="reg-mark-reviewed-btn"><Check className="h-4 w-4 mr-2" />Mark reviewed</Button>}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(selected.registration_id)} data-testid="reg-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </div>
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reg-edit-form">
                  {["surname","first_name","second_name","dob","nationality","national_insurance","email","phone_cell","phone_home","phone_work","street_address","po_box","surgery_type","surgeon","surgery_location","surgery_date","surgery_time","surgery_notes","ins_provider","ins_policy_no","ins2_provider","ins2_policy_no","kin_name","kin_relationship","kin_phone_1","kin_address"].map((k) => (
                    <Field key={k} label={k.replaceAll("_", " ")}><Input value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`reg-edit-${k}`} /></Field>
                  ))}
                  <div className="col-span-2 flex gap-2">
                    <Button onClick={saveEdit} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="reg-edit-save"><Check className="h-4 w-4 mr-2" />Save</Button>
                    <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <Sec title="Personal">
                    <KV label="DOB" value={selected.dob} /><KV label="Nationality" value={selected.nationality} />
                    <KV label="Marital" value={selected.marital_status} /><KV label="NIB" value={selected.national_insurance} />
                  </Sec>
                  <Sec title="Contact">
                    <KV label="Email" value={selected.email} /><KV label="Phone (cell)" value={selected.phone_cell} />
                    <KV label="Phone (home)" value={selected.phone_home} /><KV label="Phone (work)" value={selected.phone_work} />
                    <KV label="Address" value={selected.street_address} /><KV label="P.O. Box" value={selected.po_box} />
                  </Sec>
                  <Sec title="Employer">
                    <KV label="Employer" value={selected.employer} /><KV label="Occupation" value={selected.occupation} />
                    <KV label="Address" value={selected.employer_address} /><KV label="P.O. Box" value={selected.employer_po_box} />
                  </Sec>
                  <Sec title="Next of kin">
                    <KV label="Name" value={selected.kin_name} /><KV label="Relationship" value={selected.kin_relationship} />
                    <KV label="Phone 1" value={selected.kin_phone_1} /><KV label="Phone 2" value={selected.kin_phone_2} />
                    <KV label="Address" value={selected.kin_address} /><KV label="P.O. Box" value={selected.kin_po_box} />
                  </Sec>
                  <Sec title="Surgery">
                    <KV label="Type" value={selected.surgery_type} /><KV label="Surgeon" value={selected.surgeon} />
                    <KV label="Location" value={selected.surgery_location} /><KV label="Location type" value={selected.location_type} />
                    <KV label="Date / Time" value={`${selected.surgery_date || ""} ${selected.surgery_time || ""}`} />
                    <KV label="Notes" value={selected.surgery_notes} />
                  </Sec>
                  <Sec title="Insurance">
                    <KV label="Primary" value={selected.ins_provider} /><KV label="Policy" value={selected.ins_policy_no} />
                    <KV label="Holder" value={selected.ins_policy_holder} /><KV label="Group" value={selected.ins_group} />
                    <KV label="Secondary" value={selected.ins2_provider} /><KV label="Policy 2" value={selected.ins2_policy_no} />
                  </Sec>
                  <Sec title="Certification">
                    <KV label="Signed by" value={selected.cert_name} /><KV label="Relationship" value={selected.cert_relationship} />
                    <KV label="Date" value={selected.cert_date} />
                  </Sec>
                </>
              )}
              {selected.attachments?.length > 0 && (
                <div>
                  <h4 className="aamg-label mb-2">Attachments ({selected.attachments.length})</h4>
                  <ul className="space-y-1.5">
                    {selected.attachments.map((f, i) => (
                      <li key={f.file_id || i} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
                        <span className="truncate">{f.original_filename || `file-${i + 1}`}</span>
                        <div className="flex gap-2 shrink-0">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}`} target="_blank" rel="noreferrer" className="text-xs text-[#339999] hover:underline" data-testid={`reg-attachment-view-${i}`}>View</a>
                          <button onClick={() => downloadBlob(`/files/${f.file_id}`, f.original_filename || `file-${i + 1}`)} className="text-xs text-[#339999] hover:underline" data-testid={`reg-attachment-download-${i}`}>Download</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.signature_data && <div><h4 className="aamg-label mb-2">Signature</h4><img src={selected.signature_data} alt="" className="border border-slate-200 rounded-md max-w-xs bg-white" /></div>}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Sec({ title, children }) { return <div><h4 className="aamg-label mb-2">{title}</h4><div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div></div>; }
function KV({ label, value }) { return <div className="flex justify-between border-b border-slate-100 pb-1.5"><span className="text-xs text-slate-500">{label}</span><span className="text-sm text-slate-900 text-right">{value || "—"}</span></div>; }
function Field({ label, children }) { return <div><Label className="field-label">{label}</Label>{children}</div>; }

// -------- Procedure Requests (surgeons → anesthetists) --------
function ProcedureRequestsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const refresh = () => api.get("/admin/procedure-requests").then((r) => { setItems(r.data || []); setPicked(new Set()); });
  useEffect(() => { refresh(); }, []);
  const list = filter === "all" ? items : items.filter((r) => r.status === filter);
  const togglePick = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  const toggleAll = () => { setPicked(picked.size === list.length ? new Set() : new Set(list.map((r) => r.request_id))); };
  const del = async (rid) => { if (!window.confirm("Delete this request?")) return; await api.delete(`/admin/procedure-requests/${rid}`); toast.success("Deleted"); refresh(); setSelected(null); };
  const bulkDelete = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} request(s)?`)) return;
    const r = await api.post("/admin/procedure-requests/bulk-delete", { user_ids: Array.from(picked) });
    toast.success(`Deleted ${r.data.deleted}`); refresh();
  };
  return (
    <div className="space-y-5" data-testid="admin-procedure-requests">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">Surgeon → anesthetist</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Procedure requests</h1></div>
        <div className="flex gap-2 flex-wrap">
          {picked.size > 0 && <Button variant="outline" className="text-red-600 border-red-200" onClick={bulkDelete} data-testid="proc-req-bulk-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete {picked.size}</Button>}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="proc-req-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => downloadBlob("/admin/procedure-requests/download-excel", "procedure_requests.xlsx")} data-testid="proc-req-excel-btn"><Download className="h-4 w-4 mr-2" />Excel</Button>
        </div>
      </div>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        {list.length === 0 ? <div className="p-10 text-center text-slate-400">No requests</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={picked.size === list.length && list.length > 0} onChange={toggleAll} data-testid="proc-req-select-all" /></th>
                <th className="text-left px-4 py-2">Surgeon</th><th className="text-left px-4 py-2">Patient</th><th className="text-left px-4 py-2">Procedure</th><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Target</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>{list.map((r) => (
              <tr key={r.request_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-2"><input type="checkbox" checked={picked.has(r.request_id)} onChange={() => togglePick(r.request_id)} data-testid={`proc-req-pick-${r.request_id}`} /></td>
                <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => setSelected(r)}>{r.surgeon_name}</td>
                <td className="px-4 py-2">{r.patient_name}</td>
                <td className="px-4 py-2">{r.procedure_type}</td>
                <td className="px-4 py-2">{r.date} {r.start_time}{r.end_time && `–${r.end_time}`}</td>
                <td className="px-4 py-2">{r.target_anesthetist_id ? <span className="text-slate-700">specific</span> : <span className="text-blue-700">any</span>}</td>
                <td className="px-4 py-2"><Badge className={`border capitalize ${r.status === "accepted" ? "bg-green-50 text-green-700 border-green-200" : r.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : r.status === "declined" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</Badge></td>
                <td className="px-4 py-2 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setSelected(r)} data-testid={`view-proc-req-${r.request_id}`}>View</Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(r.request_id)} data-testid={`del-proc-req-${r.request_id}`}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="proc-req-details-dialog">
          {selected && <>
            <DialogHeader><DialogTitle className="text-xl">Procedure request — Dr. {selected.surgeon_name}</DialogTitle><DialogDescription className="text-xs text-slate-500">Surgeon → anesthetist request details</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`border capitalize ${selected.status === "accepted" ? "bg-green-50 text-green-700" : selected.status === "pending" ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700"}`}>{selected.status}</Badge>
                {selected.urgency && <Badge className={`border capitalize ${selected.urgency === "emergent" ? "bg-red-50 text-red-700 border-red-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{selected.urgency}</Badge>}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(selected.request_id)} data-testid="proc-req-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </div>
              <Sec title="Request">
                <KV label="Surgeon" value={selected.surgeon_name} /><KV label="Surgeon email" value={selected.surgeon_email} />
                <KV label="Patient" value={selected.patient_name} /><KV label="Procedure" value={selected.procedure_type} />
                <KV label="Date" value={selected.date} /><KV label="Time" value={`${selected.start_time || ""}${selected.end_time ? "–" + selected.end_time : ""}`} />
                <KV label="Location" value={selected.location} /><KV label="Urgency" value={selected.urgency} />
                <KV label="Target" value={selected.target_anesthetist_id ? "Specific" : "Any anesthetist"} />
                <KV label="Created" value={selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"} />
              </Sec>
              {selected.accepted_by_name && (
                <Sec title="Response">
                  <KV label="Accepted by" value={selected.accepted_by_name} />
                  <KV label="Accepted at" value={selected.accepted_at ? new Date(selected.accepted_at).toLocaleString() : "—"} />
                </Sec>
              )}
              {selected.notes && <div><h4 className="aamg-label mb-2">Notes</h4><div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap">{selected.notes}</div></div>}
              {selected.escalated && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">⚡ Escalated at {selected.escalated_at ? new Date(selected.escalated_at).toLocaleString() : "—"}</div>}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Emergency Cases --------
function EmergencyTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [picked, setPicked] = useState(new Set());
  const refresh = () => api.get("/admin/emergency-cases").then((r) => { setItems(r.data || []); setPicked(new Set()); });
  useEffect(() => { refresh(); }, []);
  const togglePick = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  const toggleAll = () => { setPicked(picked.size === items.length ? new Set() : new Set(items.map((r) => r.case_id))); };
  const del = async (cid) => { if (!window.confirm("Delete this case?")) return; await api.delete(`/admin/emergency-cases/${cid}`); toast.success("Deleted"); refresh(); setSelected(null); };
  const bulkDelete = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} case(s)?`)) return;
    const r = await api.post("/admin/emergency-cases/bulk-delete", { user_ids: Array.from(picked) });
    toast.success(`Deleted ${r.data.deleted}`); refresh();
  };
  const approve = async (cid) => {
    try {
      await api.post(`/admin/emergency-cases/${cid}/approve`);
      toast.success("Case approved");
      refresh();
      if (selected?.case_id === cid) setSelected({ ...selected, admin_approved: true, approved_at: new Date().toISOString() });
    } catch { toast.error("Failed to approve"); }
  };
  const unapprove = async (cid) => {
    try {
      await api.post(`/admin/emergency-cases/${cid}/unapprove`);
      toast.success("Approval removed");
      refresh();
      if (selected?.case_id === cid) setSelected({ ...selected, admin_approved: false });
    } catch { toast.error("Failed to remove approval"); }
  };
  const openEdit = () => { setEditForm({ ...selected }); setEditing(true); };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/emergency-cases/${selected.case_id}`, editForm);
      toast.success("Updated"); setEditing(false); setSelected(null); refresh();
    } catch { toast.error("Failed to update"); }
  };
  return (
    <div className="space-y-5" data-testid="admin-emergency">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">Coverage</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Emergency cases</h1></div>
        <div className="flex gap-2 flex-wrap">
          {picked.size > 0 && <Button variant="outline" className="text-red-600 border-red-200" onClick={bulkDelete} data-testid="em-bulk-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete {picked.size}</Button>}
          <Button variant="outline" onClick={() => downloadBlob("/admin/emergency-cases/download", "emergency_cases.xlsx")} data-testid="em-excel-btn"><Download className="h-4 w-4 mr-2" />Excel</Button>
        </div>
      </div>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        {items.length === 0 ? <div className="p-10 text-center text-slate-400">No cases</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={picked.size === items.length && items.length > 0} onChange={toggleAll} data-testid="em-select-all" /></th>
                <th className="text-left px-4 py-2">Patient</th><th className="text-left px-4 py-2">Procedure</th><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Requester</th><th className="text-left px-4 py-2">Accepted by</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>{items.map((c) => (
              <tr key={c.case_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-2"><input type="checkbox" checked={picked.has(c.case_id)} onChange={() => togglePick(c.case_id)} data-testid={`em-pick-${c.case_id}`} /></td>
                <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => setSelected(c)}>{c.patient_name}</td>
                <td className="px-4 py-2">{c.procedure}</td>
                <td className="px-4 py-2">{c.date_of_service}</td>
                <td className="px-4 py-2">{c.requester_name}</td>
                <td className="px-4 py-2">{c.accepted_by_name || "—"}</td>
                <td className="px-4 py-2"><Badge className={`border capitalize ${c.status === "accepted" ? "bg-green-50 text-green-700 border-green-200" : c.status === "open" || c.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{c.status}</Badge>
                  {c.admin_approved && <Badge className="ml-1 bg-[#339999]/10 text-[#339999] border-[#339999]/30 text-[10px]" data-testid={`em-approved-badge-${c.case_id}`}>Approved</Badge>}
                </td>
                <td className="px-4 py-2 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setSelected(c)} data-testid={`view-em-${c.case_id}`}>View</Button>
                  {!c.admin_approved && (
                    <Button size="sm" variant="ghost" className="text-[#339999] hover:bg-[#339999]/10" onClick={() => approve(c.case_id)} data-testid={`approve-em-${c.case_id}`} title="Approve"><Check className="h-3 w-3" /></Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(c.case_id)} data-testid={`del-em-${c.case_id}`}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(false); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="em-details-dialog">
          {selected && <>
            <DialogHeader><DialogTitle className="text-xl">Emergency case — {selected.patient_name}</DialogTitle><DialogDescription className="text-xs text-slate-500">Coverage request details</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`border capitalize ${selected.status === "accepted" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-800"}`}>{selected.status}</Badge>
                {selected.admin_approved && (
                  <Badge className="bg-[#339999]/10 text-[#339999] border-[#339999]/30" data-testid="em-approved-detail-badge">
                    <Check className="h-3 w-3 mr-1" />Admin approved
                  </Badge>
                )}
                {!selected.admin_approved ? (
                  <Button size="sm" className="bg-[#339999] hover:bg-[#2A7F7F]" onClick={() => approve(selected.case_id)} data-testid="em-approve-btn">
                    <Check className="h-4 w-4 mr-2" />Approve
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => unapprove(selected.case_id)} data-testid="em-unapprove-btn">
                    <X className="h-4 w-4 mr-2" />Remove approval
                  </Button>
                )}
                {!editing && <Button size="sm" variant="outline" onClick={openEdit} data-testid="em-edit-btn"><Plus className="h-4 w-4 mr-2 rotate-45" />Edit</Button>}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(selected.case_id)} data-testid="em-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </div>
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="em-edit-form">
                  {["patient_name","procedure","date_of_service","start_time","end_time","location","priority","status","notes"].map((k) => (
                    <Field key={k} label={k.replaceAll("_"," ")}>
                      <Input value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`em-edit-${k}`} />
                    </Field>
                  ))}
                  <div className="col-span-2 flex gap-2">
                    <Button onClick={saveEdit} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="em-edit-save"><Check className="h-4 w-4 mr-2" />Save</Button>
                    <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <Sec title="Case">
                    <KV label="Patient" value={selected.patient_name} /><KV label="Procedure" value={selected.procedure} />
                    <KV label="Date" value={selected.date_of_service} />
                    <KV label="Time" value={`${selected.start_time || ""}${selected.end_time ? "–" + selected.end_time : ""}`} />
                    <KV label="Location" value={selected.location} /><KV label="Priority" value={selected.priority} />
                  </Sec>
                  <Sec title="Requester & response">
                    <KV label="Requester" value={selected.requester_name} /><KV label="Email" value={selected.requester_email} />
                    <KV label="Accepted by" value={selected.accepted_by_name} />
                    <KV label="Accepted at" value={selected.accepted_at ? new Date(selected.accepted_at).toLocaleString() : "—"} />
                    <KV label="Created" value={selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"} />
                  </Sec>
                  {selected.notes && <div><h4 className="aamg-label mb-2">Notes</h4><div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap">{selected.notes}</div></div>}
                </>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Documents --------
function isImage(ct, name) { if (ct?.startsWith("image/")) return true; return /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(name || ""); }
function isPdf(ct, name) { if (ct === "application/pdf") return true; return /\.pdf$/i.test(name || ""); }

function DocumentsTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [previewing, setPreviewing] = useState(null); // { file_id, original_filename, content_type, url }
  const [picked, setPicked] = useState(new Set());
  const refresh = () => api.get("/admin/patient-documents").then((r) => { setItems(r.data || []); setPicked(new Set()); });
  useEffect(() => { refresh(); }, []);
  const togglePick = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  const toggleAll = () => { setPicked(picked.size === items.length ? new Set() : new Set(items.map((r) => r.document_id))); };
  const review = async (id) => { await api.post(`/admin/patient-documents/${id}/review`); toast.success("Reviewed"); refresh(); };
  const del = async (id) => { if (!window.confirm("Delete this document?")) return; await api.delete(`/admin/patient-documents/${id}`); toast.success("Deleted"); refresh(); setSelected(null); };
  const bulkDelete = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} document(s)?`)) return;
    const r = await api.post("/admin/patient-documents/bulk-delete", { user_ids: Array.from(picked) });
    toast.success(`Deleted ${r.data.deleted}`); refresh();
  };
  const openPreview = async (f) => {
    try {
      const res = await api.get(`/files/${f.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewing({ ...f, url, content_type: res.data.type || f.content_type });
    } catch { toast.error("Could not load file"); }
  };
  return (
    <div className="space-y-5" data-testid="admin-documents">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">Patient docs</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Documents</h1></div>
        <div className="flex gap-2 flex-wrap">
          {picked.size > 0 && <Button variant="outline" className="text-red-600 border-red-200" onClick={bulkDelete} data-testid="doc-bulk-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete {picked.size}</Button>}
          <Button variant="outline" onClick={() => downloadBlob("/admin/patient-documents/download-excel", "documents.xlsx")}><Download className="h-4 w-4 mr-2" />Excel</Button>
        </div>
      </div>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        {items.length === 0 ? <div className="p-10 text-center text-slate-400">No documents</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={picked.size === items.length && items.length > 0} onChange={toggleAll} data-testid="doc-select-all" /></th>
                <th className="text-left px-4 py-2">Patient</th>
                <th className="text-left px-4 py-2">Notes</th>
                <th className="text-left px-4 py-2">Files</th>
                <th className="text-left px-4 py-2">Submitted</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>{items.map((d) => (
              <tr key={d.document_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-2"><input type="checkbox" checked={picked.has(d.document_id)} onChange={() => togglePick(d.document_id)} data-testid={`doc-pick-${d.document_id}`} /></td>
                <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => setSelected(d)}>{d.patient_name}</td>
                <td className="px-4 py-2 max-w-xs"><div className="truncate text-slate-600">{d.notes || "—"}</div></td>
                <td className="px-4 py-2">{d.attachments?.length || 0}</td>
                <td className="px-4 py-2 whitespace-nowrap">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-2"><Badge className={`border capitalize ${d.status === "reviewed" ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{d.status}</Badge></td>
                <td className="px-4 py-2 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setSelected(d)} data-testid={`view-doc-${d.document_id}`}>View</Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(d.document_id)} data-testid={`del-doc-${d.document_id}`}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-details-dialog">
          {selected && <>
            <DialogHeader><DialogTitle className="text-xl">Document — {selected.patient_name}</DialogTitle><DialogDescription className="text-xs text-slate-500">Patient-submitted document and attachments</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`border capitalize ${selected.status === "reviewed" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>{selected.status}</Badge>
                {selected.status !== "reviewed" && <Button size="sm" className="bg-[#339999] hover:bg-[#2A7F7F]" onClick={() => review(selected.document_id)} data-testid="doc-mark-reviewed-btn"><Check className="h-4 w-4 mr-2" />Mark reviewed</Button>}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(selected.document_id)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </div>
              <Sec title="Submission">
                <KV label="Patient" value={selected.patient_name} /><KV label="Email" value={selected.patient_email} />
                <KV label="Submitted at" value={selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"} />
                <KV label="Files" value={selected.attachments?.length || 0} />
              </Sec>
              <div>
                <h4 className="aamg-label mb-2">Notes</h4>
                <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap">{selected.notes || "—"}</div>
              </div>
              {selected.attachments?.length > 0 ? (
                <div>
                  <h4 className="aamg-label mb-2">Attachments ({selected.attachments.length})</h4>
                  <ul className="space-y-1.5">
                    {selected.attachments.map((f, i) => (
                      <li key={f.file_id || i} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
                        <span className="truncate">{f.original_filename || `file-${i + 1}`}</span>
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => openPreview(f)} className="text-xs text-[#339999] hover:underline" data-testid={`doc-attachment-view-${i}`}>View</button>
                          <button onClick={() => downloadBlob(`/files/${f.file_id}`, f.original_filename || `file-${i + 1}`)} className="text-xs text-[#339999] hover:underline" data-testid={`doc-attachment-download-${i}`}>Download</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-sm text-slate-400 italic py-2">No attachments uploaded</div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewing} onOpenChange={(o) => { if (!o) { if (previewing?.url) URL.revokeObjectURL(previewing.url); setPreviewing(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="doc-preview-dialog">
          {previewing && <>
            <DialogHeader><DialogTitle className="text-base truncate">{previewing.original_filename}</DialogTitle><DialogDescription className="text-xs text-slate-500">Inline file preview</DialogDescription></DialogHeader>
            <div className="min-h-[400px]">
              {isImage(previewing.content_type, previewing.original_filename) ? (
                <img src={previewing.url} alt={previewing.original_filename} className="max-w-full max-h-[75vh] mx-auto" />
              ) : isPdf(previewing.content_type, previewing.original_filename) ? (
                <iframe title={previewing.original_filename} src={previewing.url} className="w-full h-[75vh] border border-slate-200 rounded" />
              ) : (
                <div className="text-center text-slate-500 p-8">
                  <p className="text-sm mb-4">Preview not available for this file type.</p>
                  <a href={previewing.url} download={previewing.original_filename} className="text-[#339999] underline">Download to view</a>
                </div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Post-Op --------
function PostOpTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [view, setView] = useState("active"); // "active" | "deleted"
  const refresh = () => {
    const path = view === "deleted" ? "/admin/postop-reviews/deleted" : "/admin/postop-reviews";
    return api.get(path).then((r) => { setItems(r.data || []); setPicked(new Set()); });
  };
  useEffect(() => { refresh(); }, [view]);
  const togglePick = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  const toggleAll = () => { setPicked(picked.size === items.length ? new Set() : new Set(items.map((r) => r.review_id))); };
  const review = async (id) => { await api.post(`/admin/postop-reviews/${id}/review`); toast.success("Reviewed"); refresh(); setSelected(null); };
  const del = async (id) => { if (!window.confirm("Delete this post-op review? It can be restored from the Deleted tab.")) return; await api.delete(`/admin/postop-reviews/${id}`); toast.success("Deleted"); refresh(); setSelected(null); };
  const restore = async (id) => { try { await api.post(`/admin/postop-reviews/${id}/restore`); toast.success("Restored"); refresh(); } catch { toast.error("Couldn't restore"); } };
  const bulkDelete = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} review(s)?`)) return;
    const r = await api.post("/admin/postop-reviews/bulk-delete", { user_ids: Array.from(picked) });
    toast.success(`Deleted ${r.data.deleted}`); refresh();
  };
  const openPreview = async (f) => {
    try {
      const res = await api.get(`/files/${f.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewing({ ...f, url, content_type: res.data.type || f.content_type });
    } catch { toast.error("Could not load file"); }
  };
  return (
    <div className="space-y-5" data-testid="admin-postop">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">Recovery</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Post-Op reviews</h1></div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-xs" role="tablist">
            <button data-testid="postop-view-active" className={`px-3 py-1.5 ${view === "active" ? "bg-[#339999] text-white" : "text-slate-600 hover:bg-slate-50"}`} onClick={() => setView("active")}>Active</button>
            <button data-testid="postop-view-deleted" className={`px-3 py-1.5 ${view === "deleted" ? "bg-[#339999] text-white" : "text-slate-600 hover:bg-slate-50"}`} onClick={() => setView("deleted")}>Deleted</button>
          </div>
          {view === "active" && picked.size > 0 && <Button variant="outline" className="text-red-600 border-red-200" onClick={bulkDelete} data-testid="postop-bulk-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete {picked.size}</Button>}
          {view === "active" && <Button variant="outline" onClick={() => downloadBlob("/admin/postop-reviews/download-excel", "postop.xlsx")}><Download className="h-4 w-4 mr-2" />Excel</Button>}
        </div>
      </div>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        {items.length === 0 ? <div className="p-10 text-center text-slate-400">No reviews</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8">{view === "active" && <input type="checkbox" checked={picked.size === items.length && items.length > 0} onChange={toggleAll} data-testid="postop-select-all" />}</th>
                <th className="text-left px-4 py-2">Patient</th>
                <th className="text-left px-4 py-2">Notes</th>
                <th className="text-left px-4 py-2">Files</th>
                <th className="text-left px-4 py-2">Submitted</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>{items.map((r) => (
              <tr key={r.review_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-2">
                  {view === "active" && <input type="checkbox" checked={picked.has(r.review_id)} onChange={() => togglePick(r.review_id)} data-testid={`postop-pick-${r.review_id}`} />}
                </td>
                <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => setSelected(r)}>{r.patient_name}</td>
                <td className="px-4 py-2 max-w-xs"><div className="truncate text-slate-600">{r.notes || "—"}</div></td>
                <td className="px-4 py-2">{r.attachments?.length || 0}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  {view === "deleted" && r.deleted_at && <div className="text-[10px] text-red-500">Deleted {r.deleted_at.slice(0,10)}{r.deleted_by_name ? ` by ${r.deleted_by_name}` : ""}</div>}
                </td>
                <td className="px-4 py-2"><Badge className={`border capitalize ${r.status === "reviewed" ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{r.status}</Badge></td>
                <td className="px-4 py-2 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setSelected(r)} data-testid={`view-postop-${r.review_id}`}>View</Button>
                  {view === "active" ? (
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(r.review_id)} data-testid={`del-postop-${r.review_id}`}><Trash2 className="h-3 w-3" /></Button>
                  ) : (
                    <Button variant="outline" size="sm" className="text-[#339999] border-[#339999]/40" onClick={() => restore(r.review_id)} data-testid={`restore-postop-${r.review_id}`}>Restore</Button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="postop-details-dialog">
          {selected && <>
            <DialogHeader><DialogTitle className="text-xl">Post-Op review — {selected.patient_name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`border capitalize ${selected.status === "reviewed" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>{selected.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => downloadBlob(`/admin/postop-reviews/${selected.review_id}/download-pdf`, `postop_${selected.review_id}.pdf`)} data-testid="postop-pdf-btn"><Download className="h-4 w-4 mr-2" />PDF</Button>
                {selected.status !== "reviewed" && <Button size="sm" className="bg-[#339999] hover:bg-[#2A7F7F]" onClick={() => review(selected.review_id)} data-testid="postop-mark-reviewed-btn"><Check className="h-4 w-4 mr-2" />Mark reviewed</Button>}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(selected.review_id)} data-testid="postop-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </div>
              <Sec title="Submission">
                <KV label="Patient" value={selected.patient_name} /><KV label="Email" value={selected.patient_email} />
                <KV label="Submitted at" value={selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"} />
                <KV label="Files" value={selected.attachments?.length || 0} />
              </Sec>
              <div>
                <h4 className="aamg-label mb-2">Notes</h4>
                <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap">{selected.notes || "—"}</div>
              </div>
              {selected.attachments?.length > 0 ? (
                <div>
                  <h4 className="aamg-label mb-2">Attachments</h4>
                  <ul className="space-y-1.5">
                    {selected.attachments.map((f, i) => (
                      <li key={f.file_id || i} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
                        <span className="truncate">{f.original_filename || `file-${i + 1}`}</span>
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => openPreview(f)} className="text-xs text-[#339999] hover:underline" data-testid={`postop-attachment-view-${i}`}>View</button>
                          <button onClick={() => downloadBlob(`/files/${f.file_id}`, f.original_filename || `file-${i + 1}`)} className="text-xs text-[#339999] hover:underline">Download</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-sm text-slate-400 italic py-2">No attachments uploaded</div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewing} onOpenChange={(o) => { if (!o) { if (previewing?.url) URL.revokeObjectURL(previewing.url); setPreviewing(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="postop-preview-dialog">
          {previewing && <>
            <DialogHeader><DialogTitle className="text-base truncate">{previewing.original_filename}</DialogTitle><DialogDescription className="text-xs text-slate-500">Inline file preview</DialogDescription></DialogHeader>
            <div className="min-h-[400px]">
              {isImage(previewing.content_type, previewing.original_filename) ? (
                <img src={previewing.url} alt={previewing.original_filename} className="max-w-full max-h-[75vh] mx-auto" />
              ) : isPdf(previewing.content_type, previewing.original_filename) ? (
                <iframe title={previewing.original_filename} src={previewing.url} className="w-full h-[75vh] border border-slate-200 rounded" />
              ) : (
                <div className="text-center text-slate-500 p-8">
                  <p className="text-sm mb-4">Preview not available for this file type.</p>
                  <a href={previewing.url} download={previewing.original_filename} className="text-[#339999] underline">Download to view</a>
                </div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Patient Ratings --------
function RatingsTab() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [picked, setPicked] = useState(new Set());
  const [view, setView] = useState("active"); // "active" | "deleted"
  const refresh = () => {
    const path = view === "deleted" ? "/admin/patient-reviews/deleted" : "/admin/patient-reviews";
    api.get(path).then((r) => { setItems(r.data || []); setPicked(new Set()); });
  };
  useEffect(() => {
    refresh();
    api.get("/admin/users", { params: { role: "anesthetist" } }).then((r) => {
      const map = {}; (r.data || []).forEach((u) => map[u.user_id] = u); setUsers(map);
    });
  }, [view]);
  const togglePick = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  const toggleAll = () => { setPicked(picked.size === items.length ? new Set() : new Set(items.map((r) => r.review_id))); };
  const del = async (rid) => {
    if (!window.confirm("Delete this rating? It will be hidden from view but retained for audit and can be restored.")) return;
    try { await api.delete(`/admin/patient-reviews/${rid}`); toast.success("Rating deleted"); refresh(); }
    catch { toast.error("Couldn't delete rating"); }
  };
  const restore = async (rid) => {
    try { await api.post(`/admin/patient-reviews/${rid}/restore`); toast.success("Rating restored"); refresh(); }
    catch { toast.error("Couldn't restore"); }
  };
  const bulkDelete = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} rating(s)?`)) return;
    try {
      const r = await api.post("/admin/patient-reviews/bulk-delete", { user_ids: Array.from(picked) });
      toast.success(`Deleted ${r.data.deleted}`);
      refresh();
    } catch { toast.error("Bulk delete failed"); }
  };
  const nameFor = (uid) => users[uid]?.name || (uid ? "Deleted user" : "Unknown");
  return (
    <div className="space-y-5" data-testid="admin-ratings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="aamg-label">Internal</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Patient ratings</h1>
          <p className="text-sm text-slate-500 mt-1">Private internal feedback from patients.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-xs" role="tablist">
            <button data-testid="ratings-view-active" className={`px-3 py-1.5 ${view === "active" ? "bg-[#339999] text-white" : "text-slate-600 hover:bg-slate-50"}`} onClick={() => setView("active")}>Active</button>
            <button data-testid="ratings-view-deleted" className={`px-3 py-1.5 ${view === "deleted" ? "bg-[#339999] text-white" : "text-slate-600 hover:bg-slate-50"}`} onClick={() => setView("deleted")}>Deleted</button>
          </div>
          {view === "active" && picked.size > 0 && (
            <Button variant="outline" className="text-red-600 border-red-200" onClick={bulkDelete} data-testid="ratings-bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" />Delete {picked.size}
            </Button>
          )}
        </div>
      </div>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        {items.length === 0 ? <div className="p-10 text-center text-slate-400">{view === "deleted" ? "No deleted ratings" : "No ratings"}</div> : (
          <>
            {view === "active" && (
              <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-3 text-xs text-slate-500">
                <input type="checkbox" checked={picked.size === items.length && items.length > 0} onChange={toggleAll} data-testid="ratings-select-all" />
                <span>Select all</span>
              </div>
            )}
            <ul className="divide-y divide-slate-100">
              {items.map((r) => (
                <li key={r.review_id} className="p-4 flex items-start gap-3" data-testid={`rating-row-${r.review_id}`}>
                  {view === "active" && (
                    <input type="checkbox" checked={picked.has(r.review_id)} onChange={() => togglePick(r.review_id)} className="mt-1" data-testid={`rating-pick-${r.review_id}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{nameFor(r.anesthetist_id)}</div>
                      <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-[#E0A96D] text-[#E0A96D]" : "text-slate-300"}`} />)}</div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {r.patient_name} · {r.created_at?.slice(0, 10)}
                      {view === "deleted" && r.deleted_at && <span className="text-red-500"> · Deleted {r.deleted_at.slice(0,10)}{r.deleted_by_name ? ` by ${r.deleted_by_name}` : ""}</span>}
                    </div>
                    {r.comment && <div className="text-sm text-slate-700 mt-2 italic">&ldquo;{r.comment}&rdquo;</div>}
                  </div>
                  {view === "active" ? (
                    <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => del(r.review_id)} data-testid={`rating-delete-${r.review_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="shrink-0 text-[#339999] border-[#339999]/40" onClick={() => restore(r.review_id)} data-testid={`rating-restore-${r.review_id}`}>
                      Restore
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent></Card>
    </div>
  );
}

// -------- Schedule (Procedure Schedule + Duty Roster) --------
function ScheduleTab() {
  const [month, setMonth] = useState(new Date());
  const [anesthetists, setAnesthetists] = useState([]);
  const [data, setData] = useState({ duty: [], blocks: [] });
  const [dayDetail, setDayDetail] = useState(null);  // date string
  const [blockDialog, setBlockDialog] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [filterAnesth, setFilterAnesth] = useState("all");
  const [requests, setRequests] = useState([]);
  const fileInput = useRef(null);
  const start = startOfMonth(month), end = endOfMonth(month);
  const days = eachDayOfInterval({ start, end });
  const sd = format(start, "yyyy-MM-dd"), ed = format(end, "yyyy-MM-dd");

  const refresh = () => api.get("/admin/schedule", { params: { start_date: sd, end_date: ed, ...(filterAnesth !== "all" ? { anesthetist_id: filterAnesth } : {}) } }).then((r) => setData(r.data || { duty: [], blocks: [] }));
  useEffect(() => {
    api.get("/admin/users", { params: { role: "anesthetist" } }).then((r) => setAnesthetists(r.data || []));
    api.get("/admin/schedule-requests").then((r) => setRequests(r.data || []));
  }, []);
  useEffect(() => { refresh(); const iv = setInterval(refresh, 5000); return () => clearInterval(iv); }, [sd, ed, filterAnesth]);

  const blocksByDate = useMemo(() => { const m = {}; data.blocks?.forEach((b) => { (m[b.date] = m[b.date] || []).push(b); }); return m; }, [data]);
  const dutyByDate = useMemo(() => { const m = {}; data.duty?.forEach((d) => { m[d.date] = d.anesthetist_ids || []; }); return m; }, [data]);
  const anesthMap = useMemo(() => { const m = {}; anesthetists.forEach((a) => { m[a.user_id] = a; }); return m; }, [anesthetists]);

  const uploadXlsx = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const res = await api.post("/admin/schedule-upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Updated ${res.data.updated_dates} dates`); refresh(); }
    catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const decide = async (rid, status) => {
    const admin_response = prompt("Optional response:") || "";
    await api.post(`/admin/schedule-requests/${rid}/decide`, { status, admin_response });
    toast.success(`Request ${status}`);
    api.get("/admin/schedule-requests").then((r) => setRequests(r.data || []));
  };

  return (
    <div className="space-y-5" data-testid="admin-schedule">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">Operations</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Procedure schedule & duty roster</h1></div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={filterAnesth} onValueChange={setFilterAnesth}>
            <SelectTrigger className="w-full sm:w-56" data-testid="schedule-filter"><SelectValue placeholder="All anesthetists" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All anesthetists</SelectItem>{anesthetists.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={fileInput} type="file" accept=".xlsx" className="hidden" onChange={uploadXlsx} />
          <Button variant="outline" onClick={() => fileInput.current?.click()} data-testid="schedule-upload-btn"><Upload className="h-4 w-4 mr-2" />Upload Excel</Button>
          <Button className="bg-[#339999] hover:bg-[#2A7F7F]" onClick={() => { setEditingBlock(null); setBlockDialog(true); }} data-testid="add-block-btn"><Plus className="h-4 w-4 mr-2" />Procedure</Button>
        </div>
      </div>

      {filterAnesth !== "all" ? (
        <Card className="aamg-card"><CardHeader><CardTitle className="text-lg">Procedures for {anesthMap[filterAnesth]?.name}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.blocks?.length === 0 ? <div className="p-10 text-center text-slate-400">No procedures</div> : (
              <ul className="divide-y divide-slate-100">
                {data.blocks?.map((b) => (
                  <li key={b.block_id} className="px-4 py-3 flex justify-between items-center">
                    <div><div className="font-medium text-slate-900">{b.date} {b.start_time}–{b.end_time}</div>
                      <div className="text-xs text-slate-500">{b.patient_name} · {b.procedure_type}</div></div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingBlock(b); setBlockDialog(true); }}>Edit</Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={async () => { await api.delete(`/admin/procedure-blocks/${b.block_id}`); refresh(); }}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent></Card>
      ) : (
        <>
          {/* Procedure schedule calendar */}
          <Card className="aamg-card">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Procedure schedule</CardTitle></CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>{format(month, "MMMM yyyy")}</h2>
                <Button variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
              <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                <div className="min-w-[560px]">
                  <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-2">
                    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-1 text-center font-medium">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array(start.getDay()).fill(null).map((_, i) => <div key={`pad-${i}`} />)}
                    {days.map((d) => {
                      const ds = format(d, "yyyy-MM-dd");
                      const dayBlocks = blocksByDate[ds] || [];
                      return (
                        <button key={ds} onClick={() => setDayDetail(ds)} data-testid={`procday-${ds}`}
                          className={`min-h-[80px] p-1.5 rounded-md border text-left ${dayBlocks.length ? "bg-blue-50/40 border-blue-200" : "bg-white border-slate-100"} hover:border-[#339999]/60`}>
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-slate-700">{format(d, "d")}</span>
                            {dayBlocks.length > 0 && <span className="text-[9px] bg-blue-600 text-white rounded-full px-1.5 py-0.5">{dayBlocks.length}</span>}
                          </div>
                          <div className="space-y-0.5 mt-1">
                            {dayBlocks.slice(0, 2).map((b) => (
                              <div key={b.block_id} className="text-[10px] truncate bg-white text-blue-800 border border-blue-100 px-1 py-0.5 rounded">{b.start_time} {b.patient_name?.split(" ")[0] || b.procedure_type}</div>
                            ))}
                            {dayBlocks.length > 2 && <div className="text-[10px] text-slate-400">+{dayBlocks.length - 2}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Duty roster calendar */}
          <Card className="aamg-card">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Duty roster</CardTitle></CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                <div className="min-w-[560px]">
                  <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-2">
                    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-1 text-center font-medium">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array(start.getDay()).fill(null).map((_, i) => <div key={`pad-d-${i}`} />)}
                    {days.map((d) => {
                      const ds = format(d, "yyyy-MM-dd");
                      const onDuty = dutyByDate[ds] || [];
                      return (
                        <button key={ds} onClick={() => setDayDetail(ds)} data-testid={`dutyday-${ds}`}
                          className={`min-h-[80px] p-1.5 rounded-md border text-left ${onDuty.length ? "bg-[#339999]/5 border-[#339999]/30" : "bg-white border-slate-100"} hover:border-[#339999]/60`}>
                          <div className="flex justify-between items-center">
                            <span className={`text-xs font-medium ${onDuty.length ? "text-[#339999]" : "text-slate-600"}`}>{format(d, "d")}</span>
                            {onDuty.length > 0 && <span className="text-[9px] bg-[#339999] text-white rounded-full px-1.5 py-0.5">{onDuty.length}</span>}
                          </div>
                          <div className="space-y-0.5 mt-1">
                            {onDuty.slice(0, 3).map((id) => (
                              <div key={id} className="text-[10px] truncate text-[#339999] font-medium">{anesthMap[id]?.name?.split(" ").slice(-1)[0] || "?"}</div>
                            ))}
                            {onDuty.length > 3 && <div className="text-[10px] text-slate-400">+{onDuty.length - 3}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Card className="aamg-card"><CardHeader><CardTitle className="text-lg">Schedule change requests</CardTitle></CardHeader>
        <CardContent>
          {requests.length === 0 ? <div className="text-sm text-slate-400">No requests</div> : (
            <ul className="divide-y divide-slate-100">{requests.map((r) => (
              <li key={r.request_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900">{r.anesthetist_name} · <span className="capitalize">{r.type?.replace("_", " ")}</span> · {r.date}</div>
                  <div className="text-xs text-slate-500">{r.reason || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`border capitalize ${r.status === "approved" ? "bg-green-50 text-green-700 border-green-200" : r.status === "denied" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{r.status}</Badge>
                  {r.status === "pending" && <>
                    <Button size="sm" className="bg-[#339999] hover:bg-[#2A7F7F]" onClick={() => decide(r.request_id, "approved")} data-testid={`approve-${r.request_id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => decide(r.request_id, "denied")}>Deny</Button>
                  </>}
                </div>
              </li>
            ))}</ul>
          )}
        </CardContent>
      </Card>

      {/* Day detail dialog */}
      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {dayDetail && <DayDetail
            date={dayDetail}
            anesthetists={anesthetists}
            blocks={blocksByDate[dayDetail] || []}
            duty={dutyByDate[dayDetail] || []}
            onChanged={refresh}
            onEditBlock={(b) => { setEditingBlock(b); setBlockDialog(true); }}
            onAddBlock={() => { setEditingBlock({ date: dayDetail }); setBlockDialog(true); }}
          />}
        </DialogContent>
      </Dialog>

      {/* Block editor */}
      <Dialog open={blockDialog} onOpenChange={(o) => { if (!o) { setBlockDialog(false); setEditingBlock(null); } }}>
        <DialogContent className="max-w-xl">
          <BlockEditor anesthetists={anesthetists} initial={editingBlock} onSaved={() => { setBlockDialog(false); setEditingBlock(null); refresh(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DayDetail({ date, anesthetists, blocks, duty, onChanged, onEditBlock, onAddBlock }) {
  const [selectedDuty, setSelectedDuty] = useState(new Set(duty));
  const toggle = (id) => { const s = new Set(selectedDuty); s.has(id) ? s.delete(id) : s.add(id); setSelectedDuty(s); };
  const saveDuty = async () => { await api.post("/admin/duty-schedule", { date, anesthetist_ids: Array.from(selectedDuty) }); toast.success("Duty saved"); onChanged(); };
  const delBlock = async (bid) => { await api.delete(`/admin/procedure-blocks/${bid}`); toast.success("Procedure deleted"); onChanged(); };

  return (
    <>
      <DialogHeader><DialogTitle className="text-xl" style={{ fontFamily: "Outfit" }}>{date}</DialogTitle></DialogHeader>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="aamg-label">Procedures on this day ({blocks.length})</h3>
          <Button size="sm" variant="outline" onClick={onAddBlock} data-testid="day-add-procedure"><Plus className="h-3 w-3 mr-1" />Add</Button>
        </div>
        {blocks.length === 0 ? <div className="text-sm text-slate-400 py-2">No procedures scheduled.</div> : (
          <ul className="space-y-2">{blocks.map((b) => {
            const a = anesthetists.find((x) => x.user_id === b.anesthetist_id);
            return (
              <li key={b.block_id} className="border border-slate-200 rounded-md p-3" data-testid={`day-block-${b.block_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{b.start_time}–{b.end_time} · {b.procedure_type || "Procedure"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{b.patient_name || "—"} · Surgeon: {b.surgeon_name || "—"} · {b.location || "—"}</div>
                    <div className="text-xs text-[#339999] mt-1">Anesthetist: {a?.name || b.anesthetist_id}</div>
                    {b.notes && <div className="text-xs text-slate-500 mt-1 italic">&ldquo;{b.notes}&rdquo;</div>}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => onEditBlock(b)} data-testid={`edit-block-${b.block_id}`}>Edit</Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => delBlock(b.block_id)} data-testid={`del-block-${b.block_id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </li>
            );
          })}</ul>
        )}
      </div>

      <div className="pt-3 border-t border-slate-200">
        <h3 className="aamg-label mb-2">On duty roster</h3>
        <ScrollArea className="max-h-48">
          <div className="space-y-1.5">{anesthetists.map((a) => (
            <label key={a.user_id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={selectedDuty.has(a.user_id)} onChange={() => toggle(a.user_id)} data-testid={`duty-check-${a.user_id}`} />
              <span className="text-sm font-medium">{a.name}</span>
              <span className="text-xs text-slate-500 ml-auto">{a.email}</span>
            </label>
          ))}</div>
        </ScrollArea>
        <Button onClick={saveDuty} className="bg-[#339999] hover:bg-[#2A7F7F] mt-3" data-testid="save-duty-btn">Save duty roster</Button>
      </div>
    </>
  );
}

function BlockEditor({ anesthetists, onSaved, initial }) {
  const isEdit = !!initial?.block_id;
  const [form, setForm] = useState({
    date: initial?.date || "",
    anesthetist_id: initial?.anesthetist_id || "",
    start_time: initial?.start_time || "",
    end_time: initial?.end_time || "",
    patient_name: initial?.patient_name || "",
    surgeon_name: initial?.surgeon_name || "",
    procedure_type: initial?.procedure_type || "",
    location: initial?.location || "",
    notes: initial?.notes || "",
  });
  const save = async () => {
    if (!form.date || !form.anesthetist_id || !form.start_time || !form.end_time) { toast.error("Fill required fields"); return; }
    if (isEdit) { await api.put(`/admin/procedure-blocks/${initial.block_id}`, form); toast.success("Procedure updated"); }
    else { await api.post("/admin/procedure-blocks", form); toast.success("Procedure added"); }
    onSaved();
  };
  return (
    <>
      <DialogHeader><DialogTitle>{isEdit ? "Edit procedure block" : "Add procedure block"}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <FieldA label="Anesthetist">
          <Select value={form.anesthetist_id} onValueChange={(v) => setForm({ ...form, anesthetist_id: v })}>
            <SelectTrigger data-testid="block-anesth"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{anesthetists.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </FieldA>
        <FieldA label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="block-date" /></FieldA>
        <FieldA label="Start"><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></FieldA>
        <FieldA label="End"><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></FieldA>
        <FieldA label="Patient"><Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} /></FieldA>
        <FieldA label="Surgeon"><Input value={form.surgeon_name} onChange={(e) => setForm({ ...form, surgeon_name: e.target.value })} /></FieldA>
        <FieldA label="Procedure"><Input value={form.procedure_type} onChange={(e) => setForm({ ...form, procedure_type: e.target.value })} /></FieldA>
        <FieldA label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></FieldA>
        <FieldA label="Notes" col={2}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FieldA>
      </div>
      <Button onClick={save} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="save-block-btn">{isEdit ? "Save changes" : "Save procedure"}</Button>
    </>
  );
}

function FieldA({ label, children, col = 1 }) {
  return <div className={col === 2 ? "col-span-2" : ""}><Label className="field-label">{label}</Label>{children}</div>;
}

// -------- Messages --------
function MessagesTab() {
  return (
    <div className="flex flex-col h-[calc(100dvh-180px)] md:h-[calc(100vh-160px)]" data-testid="admin-messages">
      <div className="shrink-0 mb-4">
        <p className="aamg-label">Care team</p>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Messages</h1>
      </div>
      <div className="flex-1 min-h-0">
        <MessagesPanel contactsEndpoint="/admin/contacts" />
      </div>
    </div>
  );
}

// -------- Users --------
function UsersTab() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "anesthetist", phone: "", bio: "" });
  const [selected, setSelected] = useState(new Set());
  const fileInput = useRef(null);
  const refresh = () => api.get("/admin/users", { params: { ...(role !== "all" ? { role } : {}), ...(q ? { q } : {}) } }).then((r) => { setItems(r.data || []); setSelected(new Set()); });
  useEffect(() => { refresh(); }, [role, q]);
  const toggleOne = (uid) => { const s = new Set(selected); s.has(uid) ? s.delete(uid) : s.add(uid); setSelected(s); };
  const toggleAll = () => { setSelected(selected.size === items.length ? new Set() : new Set(items.map((u) => u.user_id))); };
  const create = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("Required fields missing"); return; }
    try { await api.post("/admin/users", form); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "anesthetist", phone: "", bio: "" }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (u) => { await api.post(`/admin/users/${u.user_id}/activate?active=${!u.is_active}`); refresh(); };
  const deleteOne = async (u) => {
    if (!window.confirm(`Delete ${u.name}? They will be deactivated (soft delete preserves audit).`)) return;
    await api.delete(`/admin/users/${u.user_id}`); toast.success("User deleted"); refresh();
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} user(s)? Admins are protected.`)) return;
    const res = await api.post("/admin/users/bulk-delete", { user_ids: Array.from(selected) });
    toast.success(`Deleted ${res.data.deleted} user(s)`);
    refresh();
  };
  const bulkActivate = async (active) => {
    if (selected.size === 0) return;
    const res = await api.post(`/admin/users/bulk-activate?active=${active}`, { user_ids: Array.from(selected) });
    toast.success(`${active ? "Activated" : "Deactivated"} ${res.data.updated} user(s)`);
    refresh();
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const res = await api.post("/admin/users/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`Created ${res.data.created} users`); refresh(); e.target.value = "";
  };
  const allChecked = items.length > 0 && selected.size === items.length;
  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">Directory</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Users</h1></div>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8 w-56" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" /></div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All roles</SelectItem><SelectItem value="patient">Patient</SelectItem><SelectItem value="surgeon">Surgeon</SelectItem><SelectItem value="anesthetist">Anesthetist</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
          <input ref={fileInput} type="file" accept=".xlsx" className="hidden" onChange={upload} />
          <Button variant="outline" onClick={() => fileInput.current?.click()}><Upload className="h-4 w-4 mr-2" />Upload</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="create-user-btn"><Plus className="h-4 w-4 mr-2" />New user</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <FieldA label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></FieldA>
                <FieldA label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></FieldA>
                <FieldA label="Password"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></FieldA>
                <FieldA label="Role">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="anesthetist">Anesthetist</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="surgeon">Surgeon</SelectItem><SelectItem value="patient">Patient</SelectItem></SelectContent>
                  </Select>
                </FieldA>
                <FieldA label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></FieldA>
                <FieldA label="Bio"><Textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></FieldA>
              </div>
              <Button onClick={create} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="create-user-submit">Create</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bg-[#339999]/5 border border-[#339999]/30 rounded-md px-4 py-2.5 flex items-center justify-between" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-[#339999]">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => bulkActivate(true)} data-testid="bulk-activate">Activate</Button>
            <Button size="sm" variant="outline" onClick={() => bulkActivate(false)} data-testid="bulk-deactivate">Deactivate</Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={bulkDelete} data-testid="bulk-delete"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
          </div>
        </div>
      )}

      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2 w-10"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="users-select-all" /></th>
              <th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Phone</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>{items.map((u) => (
            <tr key={u.user_id} className={`border-t border-slate-100 ${selected.has(u.user_id) ? "bg-[#339999]/5" : ""}`}>
              <td className="px-4 py-2"><input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleOne(u.user_id)} data-testid={`user-check-${u.user_id}`} /></td>
              <td className="px-4 py-2 font-medium">{u.name}</td>
              <td className="px-4 py-2 text-slate-600">{u.email}</td>
              <td className="px-4 py-2 capitalize">{u.role}</td>
              <td className="px-4 py-2">{u.phone || "—"}</td>
              <td className="px-4 py-2"><Badge className={`border ${u.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{u.is_active ? "Active" : "Inactive"}</Badge></td>
              <td className="px-4 py-2"><div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.user_id}`}>{u.is_active ? "Deactivate" : "Activate"}</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteOne(u)} data-testid={`delete-${u.user_id}`}><Trash2 className="h-3 w-3" /></Button>
              </div></td>
            </tr>
          ))}</tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

// -------- Audit --------
function AuditTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [filters, setFilters] = useState({ user_id: "", action: "", resource_type: "", start_date: "", end_date: "" });
  const [hipaa, setHipaa] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const load = (page = 1) => api.get("/admin/audit-logs", { params: { page, limit: 50, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) } }).then((r) => setData({ ...r.data, page }));
  useEffect(() => { load(1); api.get("/admin/hipaa-status").then((r) => setHipaa(r.data)); }, []);
  const runMigration = async () => { setMigrating(true); try { await api.post("/admin/run-phi-migration"); toast.success("Migration completed"); api.get("/admin/hipaa-status").then((r) => setHipaa(r.data)); } finally { setMigrating(false); } };

  return (
    <div className="space-y-5" data-testid="admin-audit">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="aamg-label">HIPAA</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Audit & Security</h1></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadBlob("/admin/audit-logs/export", "audit_logs.xlsx")}><Download className="h-4 w-4 mr-2" />Export</Button>
          <Button onClick={runMigration} disabled={migrating} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="run-migration-btn">{migrating ? "Running…" : "Run PHI migration"}</Button>
        </div>
      </div>
      {hipaa && (
        <Card className="aamg-card"><CardHeader><CardTitle className="text-lg">HIPAA status</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-3 gap-4 text-sm">
            <div><div className="aamg-label">Encryption</div><div className="text-slate-900 font-medium mt-1">{hipaa.encryption}</div></div>
            <div><div className="aamg-label">Idle timeout</div><div className="text-slate-900 font-medium mt-1">{hipaa.session_idle_minutes} min</div></div>
            <div><div className="aamg-label">Absolute cap</div><div className="text-slate-900 font-medium mt-1">{hipaa.session_absolute_hours} hours</div></div>
            <div><div className="aamg-label">Audit records</div><div className="text-slate-900 font-medium mt-1">{hipaa.audit_logs_count}</div></div>
            <div className="sm:col-span-3"><div className="aamg-label">Key source</div><div className="text-slate-700 text-xs mt-1 font-mono">{hipaa.key_source}</div></div>
          </CardContent>
        </Card>
      )}
      <Card className="aamg-card"><CardHeader><CardTitle className="text-lg">Filters</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Input placeholder="user_id" value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} />
          <Input placeholder="action" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} />
          <Input placeholder="resource_type" value={filters.resource_type} onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })} />
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
          <Button onClick={() => load(1)} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="audit-filter-btn">Apply</Button>
        </CardContent>
      </Card>
      <Card className="aamg-card"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-3 py-2">Time</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Action</th><th className="text-left px-3 py-2">Resource</th><th className="text-left px-3 py-2">Details</th></tr>
          </thead>
          <tbody>{data.items.map((l) => (
            <tr key={l.audit_id} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono">{l.timestamp?.slice(0, 19).replace("T", " ")}</td>
              <td className="px-3 py-2">{l.user_email}</td>
              <td className="px-3 py-2 capitalize">{l.user_role}</td>
              <td className="px-3 py-2 font-medium">{l.action}</td>
              <td className="px-3 py-2">{l.resource_type}</td>
              <td className="px-3 py-2 text-slate-500">{l.details}</td>
            </tr>
          ))}</tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-500">{data.total} records · page {data.page}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={data.page === 1} onClick={() => load(data.page - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={data.page * 50 >= data.total} onClick={() => load(data.page + 1)}>Next</Button>
          </div>
        </div>
      </CardContent></Card>
    </div>
  );
}
